import { getPlayableUrlWithToken } from "connection/mux";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { getSessionStorageObject, setSessionStorageObject } from "utils/session-storage";

interface VideoSecurityGateProps {
	playbackUrl: string | undefined | null;
	drmExpected: boolean | undefined;
	children: React.ReactNode | React.ReactNode[] | ((state: VideoSecurityGateState) => React.ReactNode | React.ReactNode[]);
	fallback?: React.ReactNode;
	muxToken?: string;
	eventUuid?: string;
	muxDrmToken?: string;
}

type VideoSecurityGateState = {
	url: string | undefined | null;
	drmToken: string | undefined;
	drmRequired: boolean | undefined;
	playbackId: string | undefined;
	loading: boolean;
}

const initialState = (inputDrmToken?: string): VideoSecurityGateState => ({
	url: undefined,
	drmToken: inputDrmToken,
	drmRequired: undefined,
	playbackId: undefined,
	loading: true,
});

export const VideoSecurityGateContext = createContext<VideoSecurityGateState>(initialState());

export default function VideoSecurityGate({ playbackUrl, drmExpected, children, fallback, muxToken, eventUuid, muxDrmToken }: VideoSecurityGateProps) {
	const [state, setState] = useState<VideoSecurityGateState>(initialState(muxDrmToken));

	const loadTokens = useCallback(async () => {
		try {
			const stored = getSessionStorageObject(`${playbackUrl}.v2`);
			if (stored) {
				// only reuse stored if the drm stored matches the drm passed in, otherwise fetch a new one
				if (!drmExpected || (drmExpected && stored.drmToken && stored.drmToken === muxDrmToken)) { 
					setState({ ...stored, loading: false });
					return;
				}
			}

			if (muxToken) {
				// if there is a mux token passed, optimistically allow passage so there's less load time for the player
				// if the token is bad, the response will still catch it and update the playback url to be the real one
				setState(prev => ({ ...prev, loading: false, url: playbackUrl + '?token=' + muxToken, drmToken: drmExpected ? muxDrmToken : prev.drmToken, ok: true }));
			} else { 
				setState(prev => ({ ...prev, loading: true }));
			}

			const response = await getPlayableUrlWithToken(playbackUrl || "", drmExpected, muxToken, eventUuid, muxDrmToken);

			setState({
				...response,
				loading: false
			});

			if (response.ok) {
				setSessionStorageObject(`${playbackUrl}.v2`, response);
			}
		} catch (e) {
			console.error("Error loading Mux player tokens:", e);
			setState(initialState(muxDrmToken));
		} finally {
			setState(prev => ({ ...prev, loading: false }));
		}
	}, [playbackUrl, drmExpected, muxToken, eventUuid, muxDrmToken]);

	useEffect(() => {
		loadTokens();
	}, [loadTokens]);

	const content = () => {
		if (state.loading) {
			return fallback || null;
		}

		if (typeof children === "function") {
			return children(state);
		}

		return children;
	};

	return (
		<VideoSecurityGateContext.Provider value={state}>
			{content()}
		</VideoSecurityGateContext.Provider>
	);
}
